import store from '@/store'

/**
 * Storage Availability Service
 * Centralized service for checking and managing storage unit availability
 * Prevents double-booking by validating storage status before booking
 */

class StorageAvailabilityService {
  /**
   * Check if a single storage unit is available for booking
   * @param {number|string} storageId - The storage unit ID
   * @returns {boolean} - True if available, false if booked or reserved
   */
  static isStorageAvailable(storageId) {
    if (!storageId) return false

    // Get all order items from store
    const orderItems = store.getters.orderItemsEntries || []

    // Check if storage has any active bookings (moveOutDate is null)
    const activeBookings = orderItems.filter(item => {
      const itemStorageId = item.storage_id || (item.storage && item.storage.id)
      return itemStorageId === Number(storageId) && !item.moveOutDate
    })

    // Storage is available if no active bookings exist
    return activeBookings.length === 0
  }

  /**
   * Get detailed status of a storage unit
   * @param {number|string} storageId - The storage unit ID
   * @param {number|string} offerId - Optional offer ID to check if reservation belongs to this offer
   * @returns {Object} - Detailed status information
   */
  static getStorageCurrentStatus(storageId, offerId = null) {
    if (!storageId) {
      return {
        status: 'invalid',
        isAvailable: false,
        message: 'Ungültige Lager-ID'
      }
    }

    const orderItems = store.getters.orderItemsEntries || []
    const storage = store.getters.storageById(Number(storageId))

    // Find active booking for this storage
    const activeBooking = orderItems.find(item => {
      const itemStorageId = item.storage_id || (item.storage && item.storage.id)
      return itemStorageId === Number(storageId) && !item.moveOutDate
    })

    // Check if storage is reserved
    const isReserved = storage && storage.storageReservation === true

    if (activeBooking) {
      // Get customer information if available
      const order = activeBooking.order_id
        ? store.getters.orderEntries.find(o => o.id === activeBooking.order_id)
        : null

      return {
        status: 'booked',
        isAvailable: false,
        message: 'Lagereinheit ist bereits vermietet',
        booking: {
          orderItemId: activeBooking.id,
          orderId: activeBooking.order_id,
          customer: order?.customer || null,
          rentStart: activeBooking.rentStart,
          price: activeBooking.price
        }
      }
    }

    if (isReserved) {
      // Check if the reservation belongs to the current offer
      if (offerId) {
        const offer = store.getters.offerByID(offerId)
        if (offer && offer.storages) {
          const offerStorageIds = offer.storages.map(s => s.id)
          const belongsToThisOffer = offerStorageIds.includes(Number(storageId))

          if (belongsToThisOffer) {
            // Storage is reserved for THIS offer, so it's available for booking
            return {
              status: 'reserved_for_offer',
              isAvailable: true,
              message: 'Lagereinheit ist für dieses Angebot reserviert'
            }
          }
        }
      }

      // Storage is reserved but not for this offer (or no offer provided)
      return {
        status: 'reserved',
        isAvailable: false,
        message: 'Lagereinheit ist reserviert'
      }
    }

    return {
      status: 'available',
      isAvailable: true,
      message: 'Lagereinheit ist verfügbar'
    }
  }

  /**
   * Validate multiple storages for booking
   * @param {Array} storageIds - Array of storage IDs to validate
   * @param {number} offerId - Optional offer ID to check reservations
   * @returns {Object} - Validation result with conflicts if any
   */
  static async validateStoragesForBooking(storageIds, offerId = null) {
    const validationResults = {
      valid: true,
      conflicts: [],
      availableStorages: [],
      summary: ''
    }

    if (!storageIds || storageIds.length === 0) {
      validationResults.valid = false
      validationResults.summary = 'Keine Lagereinheiten ausgewählt'
      return validationResults
    }

    // Check each storage
    storageIds.forEach(storageId => {
      const status = StorageAvailabilityService.getStorageCurrentStatus(storageId, offerId)
      const storage = store.getters.storageById(Number(storageId))

      if (status.isAvailable) {
        validationResults.availableStorages.push({
          id: storageId,
          name: storage?.name || `Lager ${storageId}`,
          status: 'available'
        })
      } else {
        validationResults.valid = false
        validationResults.conflicts.push({
          storageId,
          name: storage?.name || `Lager ${storageId}`,
          reason: status.message,
          status: status.status,
          booking: status.booking || null
        })
      }
    })

    // Check if storages match offer reservation (if offer provided)
    if (offerId) {
      const offer = store.getters.offerByID(offerId)
      if (offer && offer.storages) {
        const offerStorageIds = offer.storages.map(s => s.id)
        const mismatchedStorages = storageIds.filter(id => !offerStorageIds.includes(Number(id)))

        if (mismatchedStorages.length > 0) {
          validationResults.conflicts.push({
            type: 'offer_mismatch',
            message: 'Ausgewählte Lagereinheiten stimmen nicht mit dem Angebot überein',
            storages: mismatchedStorages
          })
        }
      }
    }

    // Generate summary
    const totalStorages = storageIds.length
    const conflictCount = validationResults.conflicts.length

    if (validationResults.valid) {
      validationResults.summary = `Alle ${totalStorages} Lagereinheiten sind verfügbar`
    } else {
      validationResults.summary = `${conflictCount} von ${totalStorages} Lagereinheiten sind nicht verfügbar`
    }

    return validationResults
  }

  /**
   * Check if storage is reserved in an active offer
   * @param {number|string} storageId - The storage unit ID
   * @returns {Object|null} - Offer information if reserved, null otherwise
   */
  static checkOfferReservation(storageId) {
    const offers = store.getters.getActiveOffers || []

    const matchingOffer = offers.find(offer => offer.storages && offer.storages.some(s => s.id === Number(storageId)))

    return matchingOffer ? {
      offerId: matchingOffer.id,
      customerEmail: matchingOffer.email,
      offerDate: matchingOffer.offerDate,
      isActive: matchingOffer.isActive
    } : null
  }

  /**
   * Reserve storages temporarily during booking process
   * This is a placeholder for future implementation with backend support
   * @param {Array} storageIds - Storage IDs to reserve
   * @param {number} durationMinutes - Reservation duration in minutes
   * @returns {Object} - Reservation result
   */
  static async reserveStoragesTemporary(storageIds, durationMinutes = 15) {
    // Future implementation: Call backend to create temporary reservation
    // For now, just validate availability
    const validation = await StorageAvailabilityService.validateStoragesForBooking(storageIds)

    return {
      success: validation.valid,
      reservationId: validation.valid ? `temp_${Date.now()}` : null,
      expiresAt: validation.valid
        ? new Date(Date.now() + durationMinutes * 60 * 1000)
        : null,
      message: validation.summary,
      conflicts: validation.conflicts
    }
  }

  /**
   * Release temporary reservation
   * @param {string} reservationId - Reservation ID to release
   * @returns {boolean} - Success status
   */
  static async releaseTemporaryReservation(reservationId) {
    // Future implementation: Call backend to release reservation
    console.log(`Releasing reservation: ${reservationId}`)
    return true
  }

  /**
   * Get all currently booked storages
   * @returns {Array} - Array of booked storage IDs
   */
  static getBookedStorageIds() {
    const orderItems = store.getters.orderItemsEntries || []
    const bookedIds = new Set()

    orderItems.forEach(item => {
      if (!item.moveOutDate) {
        const storageId = item.storage_id || (item.storage && item.storage.id)
        if (storageId) {
          bookedIds.add(Number(storageId))
        }
      }
    })

    return Array.from(bookedIds)
  }

  /**
   * Check if any storages from a list are currently booked
   * @param {Array} storageIds - Array of storage IDs to check
   * @returns {boolean} - True if any storage is booked
   */
  static hasAnyBookedStorage(storageIds) {
    const bookedIds = StorageAvailabilityService.getBookedStorageIds()
    return storageIds.some(id => bookedIds.includes(Number(id)))
  }
}

// Export class to access static methods
export default StorageAvailabilityService
